import { Card, Grid, Divider, IconButton, MenuItem, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Loading, Notify } from 'notiflix';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';
// Same file as the website's built-in logo (tecnogexnew/src/assets/2c5c2526...png),
// shown here as the logo in use when no custom logo is set.
import defaultSiteLogo from 'assets/images/default-site-logo.png';
import usePermission from 'hooks/usePermission';

// Platforms the storefront footer has an icon for (keep in sync with
// SOCIAL_PLATFORMS in the backend siteSettings controller)
const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'twitter', label: 'Twitter (bird)' },
  { value: 'x', label: 'X' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'threads', label: 'Threads' },
];

// Each social link has one URL per country (keep in sync with
// SOCIAL_LINK_COUNTRIES in the backend siteSettings controller). Visitors from
// any other country get the Australia link (the India link if that one is blank).
const SOCIAL_LINK_COUNTRIES = [
  { code: 'IN', label: 'India' },
  { code: 'AU', label: 'Australia' },
];

// Shown until the admin saves its own lists (same as the storefront's built-in defaults)
const DEFAULT_LEGAL_LINKS = [
  { text: 'Legal', url: '/legal' },
  { text: 'Terms of Service', url: '/legal/terms-conditions' },
  { text: 'Privacy Policy', url: '/legal/privacy-policy' },
  { text: 'Cookie Preference', url: '/legal/cookie-preference' },
  { text: 'Refund Policy', url: '/legal/refund-policy' },
];
const DEFAULT_SOCIAL_LINKS = [
  {
    platform: 'instagram',
    url: 'https://www.instagram.com/tecnogexofficial?igsh=MTMwNW81ZHU1YXI5dA%3D%3D',
  },
  { platform: 'linkedin', url: 'https://www.linkedin.com/company/tecnogex/' },
  { platform: 'whatsapp', url: 'https://wa.me/+' },
  { platform: 'twitter', url: 'https://x.com/tecnogex_' },
  {
    platform: 'facebook',
    url: 'https://www.facebook.com/people/TecnoGex/100091787680738/',
  },
];

// Built-in defaults and rows saved before per-country links have a single
// `url` - it fills both countries until the admin changes them
const withCountryUrls = (list) =>
  list.map(({ url, countryUrls, ...rest }) => ({
    ...rest,
    countryUrls: { IN: countryUrls?.IN ?? url ?? '', AU: countryUrls?.AU ?? url ?? '' },
  }));

const moveItem = (list, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

// Site-wide settings. The logo set here is used everywhere the brand logo shows:
// storefront navbar + footer (desktop and mobile), invoices and the header of every
// email. Removing it puts each place back on its built-in default logo.
const OtherSettings = () => {
  const { user } = useSelector((state) => state.auth);
  const canUpdate = usePermission('settings', 'update');
  const [savedLogoUrl, setSavedLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [removeLogo, setRemoveLogo] = useState(false);
  const [legalLinks, setLegalLinks] = useState(DEFAULT_LEGAL_LINKS);
  const [socialLinks, setSocialLinks] = useState(() => withCountryUrls(DEFAULT_SOCIAL_LINKS));
  const [footerDirty, setFooterDirty] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${user?.accessToken}` } };

  const fetchSettings = async () => {
    try {
      Loading.circle('Loading settings...');
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/site-settings`, authHeaders);
      setSavedLogoUrl(data.data?.logoUrl || '');
      setLogoPreview(data.data?.logoUrl || '');
      if (Array.isArray(data.data?.footerLegalLinks)) setLegalLinks(data.data.footerLegalLinks);
      if (Array.isArray(data.data?.footerSocialLinks)) setSocialLinks(withCountryUrls(data.data.footerSocialLinks));
      setFooterDirty(false);
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to fetch settings');
    } finally {
      Loading.remove();
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke object URLs for picked-but-unsaved files
  useEffect(
    () => () => {
      if (logoPreview && logoPreview.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
    },
    [logoPreview],
  );

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Notify.failure('Please choose an image file');
      return;
    }
    setLogoFile(file);
    setRemoveLogo(false);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleRemove = () => {
    setLogoFile(null);
    setLogoPreview('');
    setRemoveLogo(true);
  };

  const handleSave = async () => {
    try {
      Loading.circle('Saving settings...');
      const formData = new FormData();
      if (logoFile) formData.append('logo', logoFile);
      if (removeLogo) formData.append('removeLogo', 'true');
      if (footerDirty) {
        formData.append('footerLegalLinks', JSON.stringify(legalLinks));
        formData.append('footerSocialLinks', JSON.stringify(socialLinks));
      }
      const { data } = await axios.put(`${ENDPOINT}api/v1/admin/site-settings`, formData, authHeaders);
      setSavedLogoUrl(data.data?.logoUrl || '');
      setLogoPreview(data.data?.logoUrl || '');
      setLogoFile(null);
      setRemoveLogo(false);
      if (Array.isArray(data.data?.footerLegalLinks)) setLegalLinks(data.data.footerLegalLinks);
      if (Array.isArray(data.data?.footerSocialLinks)) setSocialLinks(withCountryUrls(data.data.footerSocialLinks));
      setFooterDirty(false);
      Notify.success('Settings saved');
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to save settings');
    } finally {
      Loading.remove();
    }
  };

  const hasChanges = !!logoFile || (removeLogo && !!savedLogoUrl) || footerDirty;

  const updateLegal = (next) => {
    setLegalLinks(next);
    setFooterDirty(true);
  };
  const updateSocial = (next) => {
    setSocialLinks(next);
    setFooterDirty(true);
  };

  // Move up / move down / remove buttons for a row
  const rowActions = (list, index, update) => (
    <MDBox display="flex" alignItems="center">
      <IconButton size="small" disabled={!canUpdate || index === 0} onClick={() => update(moveItem(list, index, -1))}>
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        disabled={!canUpdate || index === list.length - 1}
        onClick={() => update(moveItem(list, index, 1))}
      >
        <ArrowDownwardIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="error"
        disabled={!canUpdate}
        onClick={() => update(list.filter((_, i) => i !== index))}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium">
                  Other Settings
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Site-wide settings used across the website and emails.
                </MDTypography>
              </MDBox>
              <Divider sx={{ my: 0 }} />

              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  Brand Logo
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  Used in the website navbar and footer (desktop and mobile), invoices and the header of every email.
                  Use a PNG or SVG with a transparent background. If no logo is set, each place shows its built-in
                  default logo.
                </MDTypography>

                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    height: 120,
                    mb: 2,
                    border: '1px dashed',
                    borderColor: 'grey.400',
                    borderRadius: 1,
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <MDBox
                    component="img"
                    src={logoPreview || defaultSiteLogo}
                    alt={logoPreview ? 'Brand logo' : 'Default logo'}
                    sx={{ maxHeight: 80, maxWidth: '80%', objectFit: 'contain' }}
                  />
                </MDBox>

                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  {logoPreview
                    ? logoFile
                      ? 'New logo selected — click Save to use it everywhere.'
                      : 'Custom logo in use.'
                    : 'Default logo in use (no custom logo uploaded).'}
                </MDTypography>

                {canUpdate && (
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton variant="outlined" color="info" component="label">
                      {logoPreview ? 'Change logo' : 'Upload logo'}
                      <input hidden type="file" accept="image/*" onChange={handleFileSelect} />
                    </MDButton>
                    {logoPreview && (
                      <MDButton variant="outlined" color="error" onClick={handleRemove}>
                        Remove logo
                      </MDButton>
                    )}
                  </MDBox>
                )}
              </MDBox>

              <Divider sx={{ my: 0 }} />

              {/* Footer bottom bar: legal links */}
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  Footer Links
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  Links in the website footer's bottom bar (desktop and mobile), in this order. Use a page path like
                  /legal/privacy-policy, or a full https:// link (opens in a new tab). Rows missing the text or link are
                  not shown.
                </MDTypography>
                {legalLinks.map((link, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MDBox key={index} display="flex" gap={1} alignItems="center" mb={1.5}>
                    <TextField
                      size="small"
                      label="Text"
                      value={link.text}
                      disabled={!canUpdate}
                      onChange={(e) =>
                        updateLegal(legalLinks.map((l, i) => (i === index ? { ...l, text: e.target.value } : l)))
                      }
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Link"
                      value={link.url}
                      disabled={!canUpdate}
                      onChange={(e) =>
                        updateLegal(legalLinks.map((l, i) => (i === index ? { ...l, url: e.target.value } : l)))
                      }
                      sx={{ flex: 1.4 }}
                    />
                    {rowActions(legalLinks, index, updateLegal)}
                  </MDBox>
                ))}
                {canUpdate && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => updateLegal([...legalLinks, { text: '', url: '' }])}
                  >
                    Add link
                  </MDButton>
                )}
              </MDBox>

              <Divider sx={{ my: 0 }} />

              {/* Footer bottom bar: social icons */}
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  Social Media Links
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  Icons in the website footer (desktop and mobile), in this order. Pick the platform for its icon and
                  paste the profile link for each country. India visitors get the India link, Australia visitors the
                  Australia link, and everyone else the Australia link (the India link if that one is blank). Rows
                  without any link are not shown.
                </MDTypography>
                {socialLinks.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MDBox key={index} display="flex" gap={1} alignItems="center" mb={1.5}>
                    <TextField
                      select
                      size="small"
                      label="Platform"
                      value={item.platform}
                      disabled={!canUpdate}
                      onChange={(e) =>
                        updateSocial(socialLinks.map((s, i) => (i === index ? { ...s, platform: e.target.value } : s)))
                      }
                      sx={{ width: 170, '& .MuiInputBase-root': { height: 40 } }}
                    >
                      {SOCIAL_PLATFORMS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {SOCIAL_LINK_COUNTRIES.map((country) => (
                      <TextField
                        key={country.code}
                        size="small"
                        label={`${country.label} link`}
                        value={item.countryUrls?.[country.code] || ''}
                        disabled={!canUpdate}
                        onChange={(e) =>
                          updateSocial(
                            socialLinks.map((s, i) =>
                              i === index
                                ? {
                                    ...s,
                                    countryUrls: {
                                      ...s.countryUrls,
                                      [country.code]: e.target.value,
                                    },
                                  }
                                : s,
                            ),
                          )
                        }
                        sx={{ flex: 1 }}
                      />
                    ))}
                    {rowActions(socialLinks, index, updateSocial)}
                  </MDBox>
                ))}
                {canUpdate && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateSocial([...socialLinks, { platform: 'instagram', countryUrls: { IN: '', AU: '' } }])
                    }
                  >
                    Add social link
                  </MDButton>
                )}
              </MDBox>

              {canUpdate && (
                <>
                  <Divider sx={{ my: 0 }} />
                  <MDBox p={3} display="flex" justifyContent="flex-end">
                    <MDButton variant="gradient" color="info" onClick={handleSave} disabled={!hasChanges}>
                      Save
                    </MDButton>
                  </MDBox>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default OtherSettings;
